import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  Play, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader,
  Settings,
  Database
} from 'lucide-react';
import * as dataService from '../../../services/dataService';

const DataProviderManagement = () => {
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState({});
  const [testingProvider, setTestingProvider] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedProviders, setExpandedProviders] = useState({});

  // Load providers and config
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading data providers...');
      console.log('Auth token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      console.log('Selected tenant:', localStorage.getItem('selectedTenant'));
      
      const [providersData, configData] = await Promise.all([
        dataService.listProviders().catch(err => {
          console.error('Failed to list providers:', err);
          console.error('Error response:', err.response);
          throw new Error(`Failed to fetch providers: ${err.message}`);
        }),
        dataService.getConfig().catch(err => {
          console.error('Failed to get config:', err);
          console.error('Error response:', err.response);
          // Config is optional for non-admin users, so just return empty
          return { providers: {} };
        })
      ]);
      
      console.log('Providers loaded:', providersData);
      console.log('Providers type:', typeof providersData);
      console.log('Providers is array:', Array.isArray(providersData));
      console.log('Providers length:', providersData?.length);
      console.log('Config loaded:', configData);
      
      setProviders(providersData || []);
      setConfig(configData || {});
    } catch (err) {
      console.error('Load data error:', err);
      
      // Provide more specific error messages
      let errorMessage = err.message;
      if (err.message.includes('Access token required')) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.message.includes('Invalid or expired token')) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to server. Please ensure the backend is running on port 3457.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Test a provider
  const handleTestProvider = async (providerId) => {
    try {
      setTestingProvider(providerId);
      setTestResults(prev => ({ ...prev, [providerId]: null }));
      
      const result = await dataService.testProvider(providerId);
      
      setTestResults(prev => ({ ...prev, [providerId]: result }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [providerId]: {
          success: false,
          message: err.message
        }
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  // Open edit dialog
  const handleEditProvider = (providerId) => {
    const providerConfig = config.providers?.[providerId] || {};
    setEditingProvider(providerId);
    setEditFormData(providerConfig);
  };

  // Close edit dialog
  const handleCloseEdit = () => {
    setEditingProvider(null);
    setEditFormData({});
  };

  // Save provider configuration
  const handleSaveProvider = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      await dataService.updateConfig(editingProvider, editFormData);
      
      setSuccess(`Configuration updated for ${editingProvider}`);
      handleCloseEdit();
      
      // Reload data
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update form data
  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle provider expansion
  const toggleProvider = (providerId) => {
    setExpandedProviders(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }));
  };

  // Get status display
  const getStatusDisplay = (provider) => {
    if (provider.enabled && provider.available) {
      return {
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900',
        icon: <CheckCircle className="w-5 h-5" />,
        text: 'Available'
      };
    } else if (provider.enabled && !provider.available) {
      return {
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900',
        icon: <AlertCircle className="w-5 h-5" />,
        text: 'Not Configured'
      };
    } else {
      return {
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-700',
        icon: <XCircle className="w-5 h-5" />,
        text: 'Disabled'
      };
    }
  };

  // Get capability badge color
  const getCapabilityColor = (capability) => {
    const colors = {
      search: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      enrich: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      scout: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return colors[capability] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  // Debug: Log render state
  console.log('🎨 RENDER STATE:', {
    loading,
    error,
    providersCount: providers.length,
    providers: providers,
    hasConfig: !!config
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Data Provider Management
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Manage external data providers for lead generation, customer enrichment, and scouting.
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-lg border border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-green-700 dark:text-green-300">{success}</p>
          </div>
        </div>
      )}

      {providers.length === 0 ? (
        <div className="p-4 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <p className="text-blue-700 dark:text-blue-300">
              No data providers configured. Check your data_api_config.json file.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {providers.map(provider => {
            const status = getStatusDisplay(provider);
            const testResult = testResults[provider.id];
            const isExpanded = expandedProviders[provider.id];
            
            return (
              <div key={provider.id} className="bg-white dark:bg-gray-900 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => toggleProvider(provider.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={status.color}>
                      {status.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {provider.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                        {status.text}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        Priority: {provider.priority}
                      </span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Provider Info */}
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Database className="w-4 h-4" />
                          Provider Information
                        </h4>
                        
                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Type:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">{provider.type}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Status:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {provider.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Available:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {provider.available ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Capabilities:</p>
                          <div className="flex flex-wrap gap-2">
                            {provider.capabilities.map(cap => (
                              <span
                                key={cap}
                                className={`px-2 py-1 rounded text-xs font-medium ${getCapabilityColor(cap)}`}
                              >
                                {cap}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Test Section */}
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          Provider Testing
                        </h4>
                        
                        <button
                          onClick={() => handleTestProvider(provider.id)}
                          disabled={testingProvider === provider.id || !provider.enabled}
                          className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {testingProvider === provider.id ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              Test Provider
                            </>
                          )}
                        </button>

                        {testResult && (
                          <div className={`mt-4 p-3 rounded-lg border ${
                            testResult.success 
                              ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800'
                              : 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                          }`}>
                            <p className={`text-sm font-medium mb-2 ${
                              testResult.success 
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-red-700 dark:text-red-300'
                            }`}>
                              {testResult.message}
                            </p>
                            {testResult.details && (
                              <pre className="text-xs overflow-auto bg-white dark:bg-gray-900 p-2 rounded mt-2">
                                {JSON.stringify(testResult.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={() => handleEditProvider(provider.id)}
                        className="py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Edit Configuration
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Provider Modal */}
      {editingProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit Provider Configuration: {editingProvider}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={editFormData.enabled || false}
                    onChange={(e) => handleFormChange('enabled', e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Enabled
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  API Key
                </label>
                <input
                  type="password"
                  value={editFormData.apiKey || ''}
                  onChange={(e) => handleFormChange('apiKey', e.target.value)}
                  placeholder="Leave blank to keep existing key"
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave blank to keep existing key
                </p>
              </div>

              {editFormData.baseUrl !== undefined && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Base URL
                  </label>
                  <input
                    type="text"
                    value={editFormData.baseUrl || ''}
                    onChange={(e) => handleFormChange('baseUrl', e.target.value)}
                    className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    value={editFormData.timeout || 30000}
                    onChange={(e) => handleFormChange('timeout', parseInt(e.target.value))}
                    className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Priority
                  </label>
                  <input
                    type="number"
                    value={editFormData.priority || 999}
                    onChange={(e) => handleFormChange('priority', parseInt(e.target.value))}
                    className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Lower number = higher priority
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleCloseEdit}
                className="py-2 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProvider}
                className="py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataProviderManagement;
